export interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  date: string;
  /** ISO date for schema + sitemap */
  isoDate: string;
  readTime: string;
  author: string;
  /** One unique image per post, used on the card and the article. */
  cardImage: string;
  imageAlt: string;
  metaTitle: string;
  metaDescription: string;
}

/**
 * Post metadata only. The article prose lives in ./blog-content, which is
 * imported solely by the article route — this file is pulled in by seo.ts and
 * therefore ends up in every page's bundle, so it is deliberately kept small.
 */
export const blogPosts: BlogPost[] = [
  {
    slug: "signs-your-drywall-needs-professional-repair",
    title: "Five Signs Your Drywall Needs More Than a Tub of Spackle",
    excerpt: "Not every wall crack is cosmetic. Here's how to tell the difference between a five-minute patch and damage that needs to be opened up and fixed properly.",
    category: "Drywall",
    date: "July 28, 2026",
    isoDate: "2026-07-28",
    readTime: "6 min read",
    author: "Handyman Luxestuardo",
    cardImage: "/assets/images/blog-drywall-repair-signs.webp",
    imageAlt: "Close-up of a cracked drywall seam above a door frame before repair",
    metaTitle: "5 Signs Your Drywall Needs Repair | Handyman Luxestuardo",
    metaDescription: "Learn which drywall cracks, stains, and seams need a real repair instead of filler, and why patching over the symptom fails. Hayward drywall repair tips.",
  },
  {
    slug: "choosing-interior-paint-colors-that-last",
    title: "Choosing Interior Paint Colors That Still Look Right in Two Years",
    excerpt: "Bay Area light changes what a color does to a room. Here's how to test a shade properly and pick a sheen that survives daily life.",
    category: "Painting",
    date: "July 9, 2026",
    isoDate: "2026-07-09",
    readTime: "7 min read",
    author: "Handyman Luxestuardo",
    cardImage: "/assets/images/blog-interior-paint-colors.webp",
    imageAlt: "Freshly painted interior room with crisp trim lines and natural light",
    metaTitle: "How to Choose Interior Paint Colors | Handyman Luxestuardo",
    metaDescription: "Test paint colors properly for Bay Area light, understand undertones, and pick the right sheen for each room. Painting advice from a Hayward handyman.",
  },
  {
    slug: "common-plumbing-problems-bay-area-homes",
    title: "Six Plumbing Problems Every Bay Area Homeowner Should Recognize",
    excerpt: "The small plumbing faults that quietly waste water and turn into expensive damage — and how to spot them before they do.",
    category: "Plumbing",
    date: "June 22, 2026",
    isoDate: "2026-06-22",
    readTime: "6 min read",
    author: "Handyman Luxestuardo",
    cardImage: "/assets/images/blog-plumbing-problems.webp",
    imageAlt: "Under-sink plumbing with shutoff valves and supply lines being inspected",
    metaTitle: "6 Common Bay Area Plumbing Problems | Handyman Luxestuardo",
    metaDescription: "Spot running toilets, seized shutoff valves, slow drains, and failing water heaters before they cause damage. Plumbing advice from a Hayward handyman.",
  },
  {
    slug: "electrical-safety-checks-older-homes",
    title: "Electrical Safety Checks Worth Doing in an Older Bay Area Home",
    excerpt: "Two-prong outlets, warm switch plates, and fixtures on brittle wiring — what's genuinely worth upgrading in a pre-1980 house.",
    category: "Electrical",
    date: "June 3, 2026",
    isoDate: "2026-06-03",
    readTime: "6 min read",
    author: "Handyman Luxestuardo",
    cardImage: "/assets/images/blog-electrical-safety.webp",
    imageAlt: "Electrical wiring and grounding connections inside a residential junction box",
    metaTitle: "Electrical Checks for Older Homes | Handyman Luxestuardo",
    metaDescription: "Two-prong outlets, missing GFCI, warm switch plates, and brittle fixture wiring — what to check in a pre-1980 Bay Area home and what to leave alone.",
  },
  {
    slug: "planning-a-bathroom-remodel",
    title: "Planning a Bathroom Remodel: Budget, Sequence, and the Parts That Matter",
    excerpt: "Where bathroom budgets actually go, how long a remodel really takes, and the one layer nobody sees that decides whether it lasts.",
    category: "Remodeling",
    date: "May 14, 2026",
    isoDate: "2026-05-14",
    readTime: "8 min read",
    author: "Handyman Luxestuardo",
    cardImage: "/assets/images/blog-bathroom-remodel-planning.webp",
    imageAlt: "Renovated bathroom with tiled shower, new vanity, and modern fixtures",
    metaTitle: "Planning a Bathroom Remodel | Handyman Luxestuardo",
    metaDescription: "Where bathroom remodel budgets really go, how long the work takes, and why waterproofing behind the tile decides whether it lasts. Hayward remodel guide.",
  },
  {
    slug: "deck-maintenance-bay-area-weather",
    title: "Deck Maintenance for Bay Area Weather: A Seasonal Guide",
    excerpt: "Wet winters near the bay and hard inland sun attack decks differently. Here's the maintenance rhythm that keeps one sound.",
    category: "Decks",
    date: "April 25, 2026",
    isoDate: "2026-04-25",
    readTime: "6 min read",
    author: "Handyman Luxestuardo",
    cardImage: "/assets/images/blog-deck-maintenance.webp",
    imageAlt: "Wooden deck boards being stained and sealed for weather protection",
    metaTitle: "Bay Area Deck Maintenance Guide | Handyman Luxestuardo",
    metaDescription: "A season-by-season deck care guide for Bay Area weather — inspection, cleaning, sealing, and knowing when repair turns into a rebuild. Hayward deck pros.",
  },
  {
    slug: "handyman-vs-specialist-contractor",
    title: "Handyman or Specialist Contractor? How to Tell Which You Need",
    excerpt: "Calling the wrong trade wastes time and money in both directions. A practical guide to which jobs suit which professional.",
    category: "Home Advice",
    date: "April 2, 2026",
    isoDate: "2026-04-02",
    readTime: "5 min read",
    author: "Handyman Luxestuardo",
    cardImage: "/assets/images/blog-handyman-vs-contractor.webp",
    imageAlt: "Carpentry and trim tools laid out on a work surface at a job site",
    metaTitle: "Handyman or Specialist Contractor? | Handyman Luxestuardo",
    metaDescription: "A practical guide to whether your job needs a handyman or a licensed specialist, where the line falls, and why calling wrong costs time and money.",
  },
];

export const getPost = (slug?: string) => blogPosts.find((p) => p.slug === slug);
